<template>
  <div class="shared-research-groups">
    <LoadingIndicator v-if="loadingRef"/>
    <template v-else-if="canListItemsRef">
      <div v-if="!sharedResearchGroupsRef.length && !visibleInvitesRef.length" class="empty-list">
        No trees are shared with this group.
      </div>
      <div v-else class="help-text">
        Members of the group can see these trees.
      </div>
      <ul v-if="sharedResearchGroupsRef.length || visibleInvitesRef.length" class="list-unstyled">
        <li v-for="inv in visibleInvitesRef" :key="inv.id">
          <ResearchGroupCard :data-group-id="inv.dataGroupId!" small select-mode :status="PendingStatus" @selected="onSelectDataGroupInvite(inv.id!)"/>
        </li>
        <li v-for="rg in sharedResearchGroupsRef" :key="rg.id">
          <ResearchGroupCard :data-group-id="rg.id!" small/>
          <div class="item-actions">
            <button type="button" v-if="canEditRef" class="btn btn-inline btn-link" @click="onRemoveResearchGroup(rg.id!)">
              Remove
            </button>
          </div>
        </li>
      </ul>
      <button type="button" v-if="showChooseMembersRef" class="btn btn-primary choose-members" @click="emit('chooseMembers')">
        Choose group members
      </button>        
      <div v-if="canEditRef" class="list-actions">
        <button type="button" v-if="sharedResearchGroupsRef.length == 0" class="btn btn-primary" @click="onShareResearchGroup">
          <PlusIcon size="1.2x"/>
          Share a tree
        </button>
        <button type="button" v-else class="btn btn-inline btn-link btn-sm" @click="onShareResearchGroup">
          Share another tree
        </button>
        <label v-if="hasClosedInvitesRef" class="form-label">
          <input type="checkbox" v-model="showClosedRequestsRef" class="form-check-input">&nbsp;
          Show approved and expired sharing requests
        </label>
      </div>
    </template>
    <div v-else class="alert alert-primary alert-sm">
      <LockIcon/> You do not have permission to see the trees shared with this group.
    </div>
    <DataGroupInviteModal ref="dataGroupInviteModalRef"/>
    <DataGroupChooseShareModal 
      ref="dataGroupChooseModalRef" 
      @requestShare="onRequestShare">
    </DataGroupChooseShareModal>
    <Modal ref="removeResearchGroupModalRef" 
      ok-style="danger" 
      @ok="removeResearchGroup">
      <template #title>Stop sharing</template>
      <div class="remove-research-group">
        <p>Stop sharing this tree with the group?</p>
        <ResearchGroupItem :data-group-id="removeDataGroupIdRef"></ResearchGroupItem>
      </div>
      <template #okText>Stop sharing</template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.shared-research-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ul {
    margin: 0 1rem;
  }
}

.empty-list {
  font-size: 0.875rem;
  color: #888;
}

.item-actions, .list-actions {
  display: flex;
  align-items: start;

  button.btn-link {
    font-size: 0.875rem;
  }
}

.choose-members {
  margin-left: 1rem;
  align-self: flex-start;
}

.list-actions {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.remove-research-group {
  min-height: 100px;

  .research-group-item {
    margin-left: 2rem;
  }
}
</style>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useDataGroupInviteStore } from '@/gp/DataGroupInviteStore'
import { InvitationResult, ItemPermissions, PrincipalType } from '@/gp/GroupAdminModel'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { AsyncData, LoadMode } from '@/util/AsyncData'
import { isDefined } from '@/util/TypeScriptUtil'
import { LockIcon, PlusIcon } from '@zhuowenli/vue-feather-icons'
import { VersionedKeyList } from '@/util/AsyncDataStore'
import { PendingStatus } from '@/util/AppUtil'
import DataGroupChooseShareModal from '@/manage/datagroups/DataGroupChooseShareModal.vue'
import DataGroupInviteModal from '@/manage/datagroups/DataGroupInviteModal.vue'
import ResearchGroupCard from '@/manage/researchgroups/ResearchGroupCard.vue'
import Modal from '@/util/Modal.vue'
import ResearchGroupItem from '../researchgroups/ResearchGroupItem.vue'
import LoadingIndicator from '@/util/LoadingIndicator.vue'

const props = defineProps<{
  familyGroupId: string
}>()

const emit = defineEmits<{
  (e: 'chooseMembers'): void
}>()

const loadingRef = ref(false)
const asyncPermissionsRef = ref<AsyncData<ItemPermissions>>()
const asyncDataGroupMemberKeyListRef = ref<AsyncData<VersionedKeyList>>()
const asyncDataGroupInviteKeyListRef = ref<AsyncData<VersionedKeyList>>()
const showClosedRequestsRef = ref(false)
const removeDataGroupIdRef = ref<string>()

const dataGroupChooseModalRef = ref<InstanceType<typeof DataGroupChooseShareModal>>()
const dataGroupInviteModalRef = ref<InstanceType<typeof DataGroupInviteModal>>()
const removeResearchGroupModalRef = ref<InstanceType<typeof Modal>>()

const familyGroupStore = useFamilyGroupStore()
const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const dataGroupInviteStore = useDataGroupInviteStore()
const workspaceStore = useWorkspaceStore()

const permissionsRef = computed(() => asyncPermissionsRef.value?.data ?? ItemPermissions.None)
const isOwnerRef = computed(() => (permissionsRef.value & ItemPermissions.Owner) > 0)
const sharedDataGroupsRef = computed(() => {
  const memberKeys = asyncDataGroupMemberKeyListRef.value?.data?.keys ?? []
  const sharedDataGroupIds = dataGroupMemberStore.getLoadedMembers(memberKeys).map(m => m.dataGroupId!) // loaded with key list
  return dataGroupStore.getLoadedGroupList(sharedDataGroupIds) // loaded with members
})
const sharedResearchGroupsRef = computed(() => sharedDataGroupsRef.value.filter(dg => !dg.isProfile))
const sharedProfilesRef = computed(() => sharedDataGroupsRef.value.filter(dg => dg.isProfile))
const invitesRef = computed(() => {
  const inviteKeys = asyncDataGroupInviteKeyListRef.value?.data?.keys ?? []
  const invites = dataGroupInviteStore.getLoadedInvites(inviteKeys) // loaded with key list
  const inviteGroups = dataGroupStore.getLoadedGroupList(invites.map(n => n.dataGroupId))
  const groupIds = new Set(inviteGroups.filter(dg => dg && !dg?.isProfile).map(rg => rg.id!))
  return invites.filter(n => groupIds.has(n.dataGroupId!))
})
const showChooseMembersRef = computed(() =>
  isOwnerRef.value && sharedResearchGroupsRef.value.length && sharedProfilesRef.value.length < 2)

const asyncWorkspaceKeyListsRef = computed(() => {
  const groupIds = sharedResearchGroupsRef.value.map(rg => rg.id!)
  groupIds.push(...invitesRef.value.map(i => i.dataGroupId!))
  // HACK: method can't actually handle multiple groups, so call it once for each group
  return groupIds.flatMap(id => workspaceStore.getAsyncKeyListsForGroups([id], LoadMode.EnsureLoaded))
})

const canListItemsRef = computed(() => (permissionsRef.value ?? 0) & ItemPermissions.ListItems)
const canEditRef = computed(() => (permissionsRef.value ?? 0) & ItemPermissions.Modify)
const visibleInvitesRef = computed(() => 
  invitesRef.value.filter(inv => showClosedRequestsRef.value || inv.result == InvitationResult.Pending))
const hasClosedInvitesRef = computed(() => 
  invitesRef.value.some(inv => inv.result != InvitationResult.Pending))

watch(props, async () => {
  loadingRef.value = true
  try {
    asyncPermissionsRef.value = familyGroupStore.getAsyncPermissions(props.familyGroupId, LoadMode.EnsureLoaded)
    asyncDataGroupMemberKeyListRef.value = dataGroupMemberStore.getAsyncKeyListsForFamilyGroups([props.familyGroupId], LoadMode.EnsureLoaded).at(0)
    asyncDataGroupInviteKeyListRef.value = dataGroupInviteStore.getAsyncKeyListForFamilyGroup(props.familyGroupId, LoadMode.EnsureLoaded)

    await Promise.all([
      asyncPermissionsRef.value?.whenLoadCompleted,
      asyncDataGroupMemberKeyListRef.value?.whenLoadCompleted, 
      asyncDataGroupInviteKeyListRef.value?.whenLoadCompleted,
    ])

    await Promise.all(asyncWorkspaceKeyListsRef.value.map(a => a.whenLoadCompleted))
  }
  finally {
    loadingRef.value = false
  }
}, { immediate: true })

function onShareResearchGroup() {
  dataGroupChooseModalRef.value?.open(
    props.familyGroupId, 
    sharedResearchGroupsRef.value.map(rg => rg.id!))
}

function onRequestShare(dataGroupId: string) {
  dataGroupInviteModalRef.value?.openNew(dataGroupId, props.familyGroupId)
}

function onSelectDataGroupInvite(inviteId: string) {
  dataGroupInviteModalRef.value?.openEdit(inviteId)
}

function onRemoveResearchGroup(dataGroupId: string) {
  removeDataGroupIdRef.value = dataGroupId
  removeResearchGroupModalRef.value?.open()
}

async function removeResearchGroup() {
  const dm = await dataGroupMemberStore.getMemberByPrincipalAsync(removeDataGroupIdRef.value!, PrincipalType.FamilyGroup, props.familyGroupId)
  if (dm) {
    await dataGroupMemberStore.deleteAsync(dm)
  }

  removeResearchGroupModalRef.value?.close()
}

</script>
